// Phase 5 (web revival) — /albums grid of album tiles.
//
// Coarser than the photo grid: 3-4 large tiles per row, each showing
// the album name, asset count, and a 2×2 mosaic of the first 4
// `recent_thumbnails` strings provided by the server. The thumbnail
// paths already have the right size param baked in by AlbumController,
// but they still require the Bearer header — so we route them through
// `prefetchAuthedImage` via a `MosaicTile` sub-component.
//
// "+ New album" opens a small modal that POSTs to /v1/albums. The
// album.created WS event invalidates qk.albums() automatically, so we
// don't optimistically update.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { api } from '../api/client';
import { prefetchAuthedImage } from '../api/client';
import type { AlbumDTO, AlbumListResponse } from '../api/types';
import { qk } from '../app/queryClient';

function AuthedTile({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    void (async () => {
      try {
        const blobURL = await prefetchAuthedImage(path);
        if (cancelled) {
          URL.revokeObjectURL(blobURL);
          return;
        }
        created = blobURL;
        setUrl(blobURL);
      } catch {
        /* leave placeholder */
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);
  return url ? (
    <img src={url} alt="" className="h-full w-full object-cover" />
  ) : (
    <div className="h-full w-full animate-pulse bg-neutral-900" />
  );
}

function CoverMosaic({ album }: { album: AlbumDTO }) {
  const thumbs = (album.recent_thumbnails ?? []).slice(0, 4);
  if (thumbs.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center bg-neutral-900 text-neutral-700">
        <span className="text-xs">Empty</span>
      </div>
    );
  }
  // Always render a 2×2 grid; if fewer than 4 thumbs, pad with empty
  // neutral cells so the layout doesn't snap.
  const slots = [thumbs[0], thumbs[1], thumbs[2], thumbs[3]];
  return (
    <div className="grid aspect-square w-full grid-cols-2 grid-rows-2 gap-[2px] bg-neutral-800">
      {slots.map((path, i) => (
        <div key={i} className="overflow-hidden bg-neutral-900">
          {path ? <AuthedTile path={path} /> : null}
        </div>
      ))}
    </div>
  );
}

function NewAlbumModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: (n: string) => api.post<AlbumDTO>('/v1/albums', { name: n }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.albums() });
      setName('');
      onClose();
    },
  });

  useEffect(() => {
    if (!open) {
      setName('');
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-80 rounded-lg border border-neutral-800 bg-neutral-950 p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-3 text-sm font-medium text-neutral-200">New album</h2>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Album name"
          className="mb-3 w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-neutral-600"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) {
              create.mutate(name.trim());
            } else if (e.key === 'Escape') {
              onClose();
            }
          }}
        />
        {create.isError && (
          <p className="mb-2 text-xs text-red-400">
            {(create.error as Error).message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate(name.trim())}
            className="rounded-md bg-neutral-200 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
          >
            {create.isPending ? '…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AlbumsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const query = useQuery({
    queryKey: qk.albums(),
    queryFn: () => api.get<AlbumListResponse>('/v1/albums'),
  });

  return (
    <div className="flex h-full flex-col bg-neutral-950">
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-white/5 px-4">
        <h1 className="font-display text-[17px] font-medium tracking-tight text-neutral-100">
          Albums
        </h1>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="rounded-full border border-white/10 px-3 py-1 text-xs font-medium text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
        >
          + New album
        </button>
      </header>
      <div className="flex-1 overflow-auto p-4">
        {query.isError ? (
          <div className="flex h-full items-center justify-center text-sm text-red-400">
            Failed to load: {(query.error as Error).message}
          </div>
        ) : query.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-neutral-500">
            Loading…
          </div>
        ) : (query.data?.albums.length ?? 0) === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-neutral-500">
            No albums yet. Click "+ New album" to start one.
          </div>
        ) : (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]">
            {query.data!.albums.map((album) => (
              <Link
                key={album.id}
                to="/album/$albumID"
                params={{ albumID: album.id }}
                className="group block overflow-hidden rounded-xl border border-white/5 bg-neutral-900/60 transition-colors hover:border-white/20"
              >
                <CoverMosaic album={album} />
                <div className="px-3.5 py-2.5">
                  <div className="truncate font-display text-[15px] font-medium tracking-tight text-neutral-100 group-hover:text-white">
                    {album.name}
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-500">
                    {album.asset_count} {album.asset_count === 1 ? 'photo' : 'photos'}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <NewAlbumModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
